import React, { useState } from 'react';
import {
  Box,
  IconButton,
  Badge,
  Dropdown,
  Menu,
  MenuButton,
  MenuItem,
  Typography,
  Divider,
  Chip,
  Sheet,
  List,
  ListItem,
  ListItemContent,
  ListItemDecorator,
  Button
} from '@mui/joy';
import {
  Notifications as NotificationsIcon,
  Circle as CircleIcon,
  CheckCircle as CheckCircleIcon,
  Assignment as AssignmentIcon,
  Update as UpdateIcon,
  Warning as WarningIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { useNotifications } from '../hooks/useNotifications';

const NotificationBell = () => {
  const { 
    notifications, 
    unreadCount, 
    loading, 
    markAsRead, 
    markAllAsRead 
  } = useNotifications();
  
  const [open, setOpen] = useState(false);

  // Get icon based on notification type
  const getNotificationIcon = (type) => {
    const iconProps = { fontSize: 'small' };
    
    switch (type) {
      case 'task_assigned':
      case 'task_reassigned':
        return <AssignmentIcon {...iconProps} color="primary" />;
      case 'task_updated':
        return <UpdateIcon {...iconProps} color="neutral" />;
      case 'task_overdue':
        return <WarningIcon {...iconProps} color="warning" />;
      default:
        return <InfoIcon {...iconProps} color="neutral" />;
    }
  };

  // Get chip color based on notification type
  const getNotificationChipColor = (type) => {
    switch (type) {
      case 'task_assigned':
      case 'task_reassigned':
        return 'primary';
      case 'task_updated':
        return 'neutral';
      case 'task_overdue':
        return 'warning';
      default:
        return 'neutral';
    }
  };

  // Format notification type for display
  const formatNotificationType = (type) => {
    return type.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  // Format time ago
  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMinutes = Math.floor((now - date) / 60000);
    
    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    
    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `${diffInHours}h ago`;
    
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) return `${diffInDays}d ago`;
    
    return date.toLocaleDateString();
  };

  const handleNotificationClick = async (notification) => {
    if (!notification.is_read) {
      await markAsRead(notification.id);
    }
    
    // Navigate to action URL if provided
    if (notification.action_url) {
      window.location.href = notification.action_url;
    }
  };

  const handleMarkAllRead = async () => {
    await markAllAsRead();
  };

  if (loading) {
    return (
      <IconButton variant="soft" size="sm" disabled>
        <NotificationsIcon />
      </IconButton>
    );
  }

  return (
    <Dropdown open={open} onOpenChange={(event, isOpen) => setOpen(isOpen)}>
      <MenuButton
        slots={{ root: IconButton }}
        slotProps={{
          root: { 
            variant: 'soft', 
            size: 'sm',
            sx: { position: 'relative' }
          }
        }}
      >
        <Badge 
          badgeContent={unreadCount} 
          color="danger" 
          size="sm"
          sx={{ 
            '& .MuiBadge-badge': { 
              fontSize: '0.625rem',
              minWidth: '16px',
              height: '16px'
            }
          }}
        >
          <NotificationsIcon />
        </Badge>
      </MenuButton>
      
      <Menu
        placement="bottom-end"
        sx={{
          minWidth: 350,
          maxWidth: 400,
          maxHeight: 500,
          overflow: 'auto'
        }}
      >
        {/* Header */}
        <Box sx={{ p: 2, pb: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography level="title-md">
              Notifications
            </Typography>
            {unreadCount > 0 && (
              <Button 
                variant="plain" 
                size="sm" 
                onClick={handleMarkAllRead}
                sx={{ fontSize: '0.75rem' }}
              >
                Mark all read
              </Button>
            )}
          </Box>
        </Box>
        
        <Divider />

        {/* Notifications List */}
        {notifications.length === 0 ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography level="body-sm" color="neutral">
              No notifications
            </Typography>
          </Box>
        ) : (
          <List sx={{ p: 0 }}>
            {notifications.map((notification, index) => (
              <ListItem
                key={notification.id}
                sx={{
                  cursor: 'pointer',
                  bgcolor: notification.is_read ? 'transparent' : 'primary.50',
                  '&:hover': {
                    bgcolor: notification.is_read ? 'neutral.50' : 'primary.100'
                  },
                  py: 1.5,
                  px: 2,
                  borderBottom: index < notifications.length - 1 ? '1px solid' : 'none',
                  borderColor: 'divider'
                }}
                onClick={() => handleNotificationClick(notification)}
              >
                  <ListItemDecorator sx={{ minWidth: 'auto', mr: 1.5 }}>
                    {getNotificationIcon(notification.notification_type)}
                  </ListItemDecorator>
                  
                  <ListItemContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
                      <Typography 
                        level="title-sm" 
                        sx={{ 
                          fontWeight: notification.is_read ? 'normal' : 'bold',
                          fontSize: '0.875rem'
                        }}
                      >
                        {notification.title}
                      </Typography>
                      {!notification.is_read && (
                        <CircleIcon sx={{ fontSize: 8, color: 'primary.500', ml: 1 }} />
                      )}
                    </Box>
                    
                    <Typography 
                      level="body-sm" 
                      color="neutral" 
                      sx={{ mb: 1, fontSize: '0.75rem' }}
                    >
                      {notification.message}
                    </Typography>
                    
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Chip 
                        size="sm" 
                        variant="soft" 
                        color={getNotificationChipColor(notification.notification_type)}
                        sx={{ fontSize: '0.625rem' }}
                      >
                        {formatNotificationType(notification.notification_type)}
                      </Chip>
                      
                      <Typography level="body-xs" color="neutral">
                        {formatTimeAgo(notification.created_at)}
                      </Typography>
                    </Box>
                  </ListItemContent>
                </ListItem>
            ))}
          </List>
        )}
        
        {notifications.length > 5 && (
          <>
            <Divider />
            <Box sx={{ p: 1, textAlign: 'center' }}>
              <Button 
                variant="plain" 
                size="sm" 
                fullWidth
                onClick={() => {
                  // Navigate to full notifications page
                  window.location.href = '/notifications';
                  setOpen(false);
                }}
              >
                View all notifications
              </Button>
            </Box>
          </>
        )}
      </Menu>
    </Dropdown>
  );
};

export default NotificationBell;
